import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";

import { useQuery } from "@tanstack/react-query";
import { isBefore } from "date-fns";
import {
  Accordion,
  Button,
  Dimmer,
  Divider,
  Header,
  Icon,
  Loader,
  Message,
  SemanticICONS,
} from "semantic-ui-react";
import { styled, useTheme } from "styled-components";

import { handleClickForKeyboard } from "simplydo/core/accessibility";
import { formatDate, formatSecondsToDuration } from "simplydo/core/dates";
import { initUseQuery } from "simplydo/core/query";
import { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";
import { AccessibleDropdown } from "simplydo/react/components/Accessibility/AccessibleDropdown";
import { AccessibleModal } from "simplydo/react/components/Accessibility/AccessibleModal";
import { MarkdownViewer } from "simplydo/react/richtext";

import api from "api";
import { useAppSelector } from "store";
import util from "utils/utils";

import IdeaTags from "components/ideas/Tags";
import ModalCarousel, {
  type ModalCarouselScreen,
} from "components/lib/ModalCarousel";
import { TooltipButton } from "components/lib/UI";

const StyledButton = styled(Button)`
  min-width: max-content;
`;

/** Steps included for the current wizard session (fixed when the carousel opens). */
type SubmissionWizardSteps = {
  name: boolean;
  tags: boolean;
  aiFeedback: boolean;
};

type IdeaSubmissionNextStepProps = {
  to?: string;
  icon: string;
  text: string;
  onClick?: () => void;
};

const IdeaSubmissionNextStep = ({
  to,
  icon,
  text,
  onClick,
}: IdeaSubmissionNextStepProps) => {
  return (
    <div style={{ marginBottom: 25, fontSize: "larger", cursor: "pointer" }}>
      <Button as={to ? Link : undefined} to={to} secondary onClick={onClick}>
        <Icon style={{ paddingRight: 3 }} name={icon as SemanticICONS} />
        {text}
      </Button>
    </div>
  );
};

type SubmitIdeaButtonProps = {
  updateIdea: (data: any, fields: string[], save?: boolean) => void;
  onSubmit: (toSubmit: boolean) => void;
  isSaving?: boolean;
  style?: React.CSSProperties;
  size?: "tiny" | "small" | "medium" | "large" | "big" | "huge" | "massive";
  idea: OpenAPI.Assign<
    OpenAPI.Schemas["Idea"],
    OpenAPI.Schemas["AssignIdeaTemplates"],
    OpenAPI.Schemas["AssignIdeaChallenges"],
    OpenAPI.Schemas["AssignIdeaSubmissibilityStatus"]
  >;
  unsaved?: boolean;
  isStatusBar?: boolean;
  reloadIdea: () => void;
};

const SubmitIdeaButton = ({
  updateIdea,
  onSubmit,
  isSaving,
  style,
  size = "tiny",
  idea,
  unsaved,
  isStatusBar,
}: SubmitIdeaButtonProps) => {
  const { t } = useTranslation();
  const user = useAppSelector((state) => state.user);
  const navigate = useNavigate();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionWizardOpen, setSubmissionWizardOpen] = useState(false);
  const [wizardSteps, setWizardSteps] = useState<SubmissionWizardSteps | null>(
    null,
  );
  const [submitModalOpen, setSubmitModalOpen] = useState(false);
  const [showAiFeedback, setShowAiFeedback] = useState(true);
  const [loadingAIIdeaName, setLoadingAIIdeaName] = useState(false);
  const theme = useTheme();

  const userId = user?._id;

  const superadminBypassesAiFeedbackCooldown = useMemo(
    () => util.hasPermission(user, "super.viewDashboard"),
    [user],
  );

  const canEditIdea = util.canEditIdea(user, idea);
  const canManageChallenge = util.canManageChallenge(user, idea.ownerChallenge);
  const canUpdateIdeaStatus = util.hasPermission(
    user,
    "challenge.updateIdeaStatus",
    idea.challenge,
  );
  const canEditIdeas = util.hasPermission(
    user,
    "challenge.editIdeas",
    idea.challenge,
  );
  const canManageChallenges = util.hasPermission(
    user,
    "org.manageChallenges",
    idea.ownerChallenge?.organisation,
  );

  const aiIdeaFeedbackQueryEnabled =
    !!userId &&
    canEditIdea &&
    !!idea?.ownerChallenge?.aiIdeaFeedback &&
    !idea?.isSubmitted;

  const aiFeedbackTaskQuery = useQuery(
    initUseQuery(
      api.users.getLatestLongRunningTask,
      [userId, "idea", idea?._id, "aiIdeaFeedback"],
      {
        isEnabled: aiIdeaFeedbackQueryEnabled,
        refetchInterval: (data) => {
          if (data.state?.data?.task?.status === "pending") {
            return 3000;
          }
          return false;
        },
        onSuccess: (data) => {
          if (data?.task?.status === "success") {
            api.invalidateQueries(api.ideas.getAiIdeaFeedback, [idea._id]);
            setShowAiFeedback(true);
          }
        },
      },
    ),
  );
  const aiFeedbackTask = aiFeedbackTaskQuery.data?.task;

  const aiIdeaFeedbackQuery = useQuery(
    initUseQuery(api.ideas.getAiIdeaFeedback, [idea._id], {
      isEnabled: aiIdeaFeedbackQueryEnabled,
    }),
  );
  const aiFeedback = aiIdeaFeedbackQuery.data?.aiIdeaFeedback ?? null;

  const canSubmitWithRequiredFieldsMissing = useMemo(
    () => canManageChallenges || canEditIdeas,
    [canManageChallenges, canEditIdeas],
  );

  const defaultName = `${t("common:capitalise", { key: "generic.idea" })} for ${idea.ownerChallenge?.name}`;
  const isDefaultName = idea && idea.name === defaultName;

  const isSubmissible = idea?.submissibilityStatus?.isSubmissible;
  const tagBeforeSubmission = idea?.ownerChallenge?.tagBeforeSubmission;
  const hasTags = (idea?.tags || []).length > 0;
  const hasTagList = (idea?.ownerChallenge?.tagList ?? []).length > 0;

  const challengeIsClosed = idea?.ownerChallenge?.stage === "closed";
  const submissionDeadline = idea?.submissionDeadline?.deadline;

  const tooltip = useMemo(() => {
    if (
      challengeIsClosed &&
      (!submissionDeadline ||
        (submissionDeadline &&
          isBefore(new Date(submissionDeadline), new Date())))
    ) {
      return `This ${t("generic.challenge")} is now closed to submissions.`;
    }
    return null;
  }, [challengeIsClosed, submissionDeadline, t]);

  const loadAIIdeaName = useCallback(() => {
    setLoadingAIIdeaName(true);
    api.ideas.createIdeaNameWithAI(
      idea._id,
      ({ success, name: aiName }) => {
        if (success) {
          updateIdea(aiName, ["name"]);
        }
        setLoadingAIIdeaName(false);
      },
      () => {
        setLoadingAIIdeaName(false);
      },
    );
  }, [idea._id, updateIdea]);

  const ideaName = idea?.name;
  const isSubmitted = idea?.isSubmitted;
  const ownerChallengeName = idea?.ownerChallenge?.name;

  const needsName = useMemo(
    () =>
      !isSubmitted &&
      (!ideaName ||
        ideaName ===
          `${t("common:capitalise", { key: "generic.idea" })} for ${ownerChallengeName}`),
    [isSubmitted, ideaName, ownerChallengeName, t],
  );

  const showTagsStep =
    tagBeforeSubmission && !hasTags && hasTagList && !isSubmitted;

  const showAiFeedbackStep =
    !!idea?.ownerChallenge?.aiIdeaFeedback && !idea?.isSubmitted;

  const submitLogic = useCallback(
    (toSubmit: boolean, bypassConfirmation = false) => {
      const doSubmit = () => {
        const upd: OpenAPI.PUT<"/ideas/{id}/status">["requestBody"] = {
          isSubmitted: toSubmit,
        };
        setIsSubmitting(true);
        api.ideas.updateStatus(
          idea._id,
          upd,
          (newIdea) => {
            util.onUpdateIdea(newIdea, newIdea);

            if (newIdea.isSubmitted) {
              setSubmitModalOpen(true);
              onSubmit(toSubmit);
              setSubmissionWizardOpen(false);
              setWizardSteps(null);
              setIsSubmitting(false);
            } else {
              toast.success(t("ideas.submit.confirm.unsubmitSuccess"));
              onSubmit(toSubmit);
              setSubmissionWizardOpen(false);
              setWizardSteps(null);
              setIsSubmitting(false);
            }

            api.invalidateQueries(api.challenges.getIdeas, [newIdea.challenge]);
          },
          (err) => {
            if (err.status === 400) {
              updateIdea("closed", ["ownerChallenge", "stage"], true);
            }
            toast.error(err.message);
            setIsSubmitting(false);
          },
        );
      };

      if (toSubmit && bypassConfirmation) {
        doSubmit();
        return;
      }

      if (!toSubmit) {
        util
          .confirm(
            t("ideas.submit.confirm.withdraw.title"),
            t("ideas.submit.confirm.withdraw.info"),
          )
          .then(() => {
            doSubmit();
          })
          .catch(() => {});
      }
    },
    [t, onSubmit, updateIdea, idea._id],
  );

  const getAIIdeaFeedbackCooldownSeconds = useCallback(() => {
    if (!aiFeedback?.generatedAt) {
      return null;
    }

    const generatedAt = new Date(aiFeedback.generatedAt);
    const requestCount = aiFeedback.requestCount || 0;

    const waitWindows = [5, 30, 60, 1440];
    const waitIndex = Math.min(
      Math.max(requestCount - 1, 0),
      waitWindows.length - 1,
    );
    const waitMinutes =
      waitWindows[waitIndex] ?? waitWindows[waitWindows.length - 1]!;
    const nextAllowedAt = generatedAt.getTime() + waitMinutes * 60 * 1000;
    const remainingMs = nextAllowedAt - Date.now();
    if (remainingMs <= 0) {
      return null;
    }
    return Math.ceil(remainingMs / 1000);
  }, [aiFeedback]);

  const [aiIdeaFeedbackCooldownSeconds, setAiIdeaFeedbackCooldownSeconds] =
    useState<number | null>(getAIIdeaFeedbackCooldownSeconds());

  useEffect(() => {
    if (!submissionWizardOpen || !wizardSteps?.aiFeedback) {
      return;
    }

    const updateCooldown = () => {
      setAiIdeaFeedbackCooldownSeconds(getAIIdeaFeedbackCooldownSeconds());
    };

    updateCooldown();
    const interval = setInterval(updateCooldown, 1000);
    return () => clearInterval(interval);
  }, [
    submissionWizardOpen,
    wizardSteps?.aiFeedback,
    getAIIdeaFeedbackCooldownSeconds,
    aiFeedback,
  ]);

  const aiFeedbackBusy =
    aiFeedbackTaskQuery.isLoading || aiFeedbackTask?.status === "pending";

  const requestAIIdeaFeedback = useCallback(() => {
    if (!user?._id) {
      return;
    }
    api.users.createLongRunningTask(
      user._id,
      { forType: "idea", forId: idea._id, type: "aiIdeaFeedback" },
      () => {
        api.invalidateQueries(api.users.getLatestLongRunningTask, [
          user._id,
          "idea",
          idea._id,
          "aiIdeaFeedback",
        ]);
        api.invalidateQueries(api.ideas.getAiIdeaFeedback, [idea._id]);
        setAiIdeaFeedbackCooldownSeconds(getAIIdeaFeedbackCooldownSeconds());
      },
      (err) => {
        if (err.status === 429) {
          const match = err.message?.match(/(\d+)/);
          if (match && !superadminBypassesAiFeedbackCooldown) {
            toast.error(
              `Please wait ${match[1]} seconds before requesting another review.`,
            );
          } else {
            toast.error(err.message);
          }
        } else {
          toast.error(err.message);
        }
      },
    );
  }, [
    user?._id,
    idea._id,
    getAIIdeaFeedbackCooldownSeconds,
    superadminBypassesAiFeedbackCooldown,
  ]);

  const closeSubmissionWizard = useCallback(() => {
    setSubmissionWizardOpen(false);
    setWizardSteps(null);
  }, []);

  const openSubmissionWizard = useCallback(() => {
    setWizardSteps({
      name: needsName,
      tags: showTagsStep,
      aiFeedback: showAiFeedbackStep,
    });
    if (needsName) {
      loadAIIdeaName();
    }
    setShowAiFeedback(false);
    setSubmissionWizardOpen(true);
  }, [needsName, showTagsStep, showAiFeedbackStep, loadAIIdeaName]);

  const submitIdea = useCallback(
    (toSubmit: boolean) => {
      if (toSubmit) {
        openSubmissionWizard();
      } else {
        submitLogic(false);
      }
    },
    [openSubmissionWizard, submitLogic],
  );

  const confirmSubmitTitle =
    !isSubmissible && canSubmitWithRequiredFieldsMissing ?
      t("ideas.submit.confirm.submit.skipRequiredTitle")
    : t("ideas.submit.confirm.submit.title");

  const confirmSubmitInfo =
    !isSubmissible && canSubmitWithRequiredFieldsMissing ?
      t("ideas.submit.confirm.submit.skipRequiredInfo")
    : t("ideas.submit.confirm.submit.info");

  const submissionScreens = useMemo((): ModalCarouselScreen[] => {
    if (!wizardSteps) {
      return [];
    }

    const screens: ModalCarouselScreen[] = [];

    if (wizardSteps.name) {
      screens.push({
        title: t("ideas.submit.naming.title"),
        disableNext:
          loadingAIIdeaName ||
          !idea?.name ||
          isDefaultName ||
          !!isSaving ||
          !!unsaved,
        content:
          loadingAIIdeaName ?
            <div style={{ padding: "20px 0" }}>
              <Dimmer active inverted>
                <Loader
                  active
                  content={`Generating ${t("generic.idea")} name...`}
                  inverted
                />
              </Dimmer>
            </div>
          : <>
              <p>
                {t("ideas.submit.naming.info")}
                {". "}
                {`${t("ideas.submit.naming.help")}`}
              </p>
              <Input
                placeholder={t("ideas.submit.naming.placeholder")}
                value={isDefaultName ? "" : idea?.name}
                fluid
                onChange={(e) =>
                  updateIdea(e.target.value || defaultName, ["name"])
                }
                style={{ minWidth: theme.sizes.isMobile ? "80vw" : 400 }}
              />
            </>,
      });
    }

    if (wizardSteps.tags) {
      screens.push({
        title: `${t("common:capitalise", { key: "generic.idea" })} tags`,
        disableNext: !hasTags,
        content: (
          <>
            <p>{t("ideas.statusBar.tag.info")}</p>
            <IdeaTags idea={idea} updateIdea={updateIdea} isSubmission={true} />
          </>
        ),
      });
    }

    if (wizardSteps.aiFeedback) {
      screens.push({
        title: `Automated ${t("generic.idea")} review`,
        content: (
          <>
            <p>
              SimplyDo can review your {t("generic.idea")} against the brief.
              This can be useful for helping to detect any issues ahead of any
              subsequent assessment.
            </p>
            {aiFeedbackBusy ?
              <Message>
                Generating new automated {t("generic.idea")} feedback...
              </Message>
            : null}
            {aiFeedback?.generatedAt && (
              <Message style={{ marginTop: 16 }}>
                <Accordion>
                  <Accordion.Title
                    active={showAiFeedback}
                    index={0}
                    onClick={() => {
                      setShowAiFeedback((open) => !open);
                    }}
                  >
                    <p>
                      <Icon name="dropdown" />
                      {showAiFeedback ?
                        `${t("common:capitalise", { key: "generic.idea" })} review`
                      : `Show automated ${t("generic.idea")} review`}
                    </p>
                  </Accordion.Title>
                  <Accordion.Content active={showAiFeedback} index={0}>
                    <Message.Content>
                      <p
                        style={{
                          marginBottom: 8,
                          color: "rgba(0, 0, 0, 0.6)",
                          fontSize: "0.9em",
                        }}
                      >
                        Previously generated on:{" "}
                        {formatDate(new Date(aiFeedback!.generatedAt!))}
                      </p>
                      {(
                        aiFeedback?.briefFitScore &&
                        aiFeedback.briefFitScore >= 0.8
                      ) ?
                        <p
                          style={{
                            marginBottom: 8,
                            color: "rgba(0, 0, 0, 0.75)",
                            fontSize: "0.95em",
                          }}
                        >
                          Our automated review found that your{" "}
                          {t("generic.idea")} generally aligns with the brief,
                          so there is no additional feedback at this time.
                          Please note that this does not replace a manual
                          review, and we recommend reviewing your{" "}
                          {t("generic.idea")} carefully before submission.
                        </p>
                      : <>
                          {aiFeedback?.content && (
                            <MarkdownViewer value={aiFeedback.content} />
                          )}
                          {(aiFeedback?.strengths?.length ?? 0) > 0 && (
                            <>
                              <Header
                                as="h4"
                                style={{ marginTop: 16, marginBottom: 8 }}
                              >
                                Strengths
                              </Header>
                              <ul>
                                {aiFeedback?.strengths?.map((item, index) => (
                                  <li key={`strength-${index}`}>{item}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {(aiFeedback?.weaknesses?.length ?? 0) > 0 && (
                            <>
                              <Header
                                as="h4"
                                style={{ marginTop: 16, marginBottom: 8 }}
                              >
                                Weaknesses
                              </Header>
                              <ul>
                                {aiFeedback?.weaknesses?.map((item, index) => (
                                  <li key={`weakness-${index}`}>{item}</li>
                                ))}
                              </ul>
                            </>
                          )}
                          {(aiFeedback?.missingOrPartialAnswers?.length ?? 0) >
                            0 && (
                            <>
                              <Header
                                as="h4"
                                style={{ marginTop: 16, marginBottom: 8 }}
                              >
                                Missing or partial answers
                              </Header>
                              <ul>
                                {aiFeedback?.missingOrPartialAnswers?.map(
                                  (item, index) => (
                                    <li key={`missing-${index}`}>{item}</li>
                                  ),
                                )}
                              </ul>
                            </>
                          )}
                          {(aiFeedback?.recommendations?.length ?? 0) > 0 && (
                            <>
                              <Header
                                as="h4"
                                style={{ marginTop: 16, marginBottom: 8 }}
                              >
                                Recommendations
                              </Header>
                              <ul>
                                {aiFeedback?.recommendations?.map(
                                  (item, index) => (
                                    <li key={`recommendation-${index}`}>
                                      {item}
                                    </li>
                                  ),
                                )}
                              </ul>
                            </>
                          )}
                        </>
                      }
                    </Message.Content>
                  </Accordion.Content>
                </Accordion>
              </Message>
            )}
            <div style={{ marginTop: 16 }}>
              {(
                !aiFeedbackBusy &&
                !superadminBypassesAiFeedbackCooldown &&
                !!aiIdeaFeedbackCooldownSeconds
              ) ?
                <p
                  style={{
                    marginBottom: 8,
                    color: "rgba(0, 0, 0, 0.6)",
                    fontSize: "0.95em",
                  }}
                >
                  You can request another review in{" "}
                  {formatSecondsToDuration(aiIdeaFeedbackCooldownSeconds, {
                    format: { hours: "h", minutes: "m", seconds: "s" },
                  })}
                  .
                </p>
              : null}
              <Button
                content={`Review my ${t("generic.idea")}`}
                loading={aiFeedbackBusy}
                primary
                disabled={
                  aiFeedbackBusy ||
                  (!!aiIdeaFeedbackCooldownSeconds &&
                    !superadminBypassesAiFeedbackCooldown)
                }
                onClick={requestAIIdeaFeedback}
              />
            </div>
          </>
        ),
      });
    }

    screens.push({
      title: confirmSubmitTitle,
      content: <p>{confirmSubmitInfo}</p>,
      actions: [
        {
          label: t("generic.submit"),
          icon: isSubmitting ? "spinner" : "paper plane",
          primary: true,
          loading: isSubmitting,
          disabled: isSaving || unsaved,
          onClick: () => submitLogic(true, true),
        },
      ],
    });

    return screens;
  }, [
    wizardSteps,
    loadingAIIdeaName,
    idea,
    isDefaultName,
    isSaving,
    unsaved,
    t,
    updateIdea,
    defaultName,
    theme.sizes.isMobile,
    hasTags,
    showAiFeedback,
    aiFeedbackBusy,
    aiFeedback,
    superadminBypassesAiFeedbackCooldown,
    aiIdeaFeedbackCooldownSeconds,
    requestAIIdeaFeedback,
    confirmSubmitTitle,
    confirmSubmitInfo,
    isSubmitting,
    submitLogic,
  ]);

  const copyLink = useCallback(() => {
    if (window.navigator?.clipboard) {
      window.navigator.clipboard
        .writeText(
          `${util.getBaseUrl(user?.ownerOrganisation)}/ideas/${idea._id}`,
        )
        .then(
          () => toast.success("Link copied to your clipboard"),
          () => toast.error("Unable to copy the link"),
        );
    } else {
      toast.error("Your browser does not support copying");
    }
  }, [idea, user]);

  return (
    <>
      <ModalCarousel
        open={submissionWizardOpen}
        onClose={closeSubmissionWizard}
        scrolling={!!wizardSteps?.aiFeedback}
        screens={submissionScreens}
        actions={[
          { label: "Continue editing", onClick: closeSubmissionWizard },
        ]}
      />

      <AccessibleModal
        mountNode={document.getElementById("semantic-modal-mount-node")}
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
      >
        <div
          style={{
            textAlign: "center",
            marginTop: 7,
            marginBottom: 10,
            paddingLeft: 20,
            paddingRight: 20,
          }}
        >
          <div style={{ marginTop: 40, marginBottom: 20 }}>
            <Icon name="checkmark" size="huge" color="green" />
          </div>
          <Header as="h2" style={{ marginBottom: 20 }}>
            <small>
              Your {t("generic.idea")} was successfully submitted to{" "}
              {idea?.ownerChallenge?.name}
            </small>
          </Header>
          {(
            idea?.ownerChallenge &&
            (idea.ownerChallenge.ideaVisibility !== "users" ||
              !!idea.ownerChallenge?.ideaVisibilityLimits?.length)
          ) ?
            null
          : <>
              <Divider hidden />
              <Divider horizontal>
                <Header as="h4">{t("challenges.new.done.info")}</Header>
              </Divider>
              <AccessibleModal.Content>
                <div
                  style={{
                    margin: 30,
                    display: theme.sizes.isMobile ? null : "flex",
                    justifyContent: "space-around",
                  }}
                >
                  {user?._id ?
                    <IdeaSubmissionNextStep
                      to={"/challenges/discover"}
                      icon="eye"
                      text={`Discover ${t("generic.challenges")}`}
                    />
                  : null}
                  {idea.ownerChallenge && (
                    <IdeaSubmissionNextStep
                      to={`/challenges/${idea.ownerChallenge?._id}`}
                      icon="target"
                      text={`View the ${t("generic.challenge")}`}
                    />
                  )}
                  <AccessibleDropdown
                    icon={null}
                    style={{ minWidth: "max-content" }}
                    pointing="top right"
                    trigger={
                      <IdeaSubmissionNextStep
                        icon="share"
                        text="Share your idea"
                      />
                    }
                    options={[
                      {
                        key: "share-x",
                        value: "share-x",
                        text: "Share on X",
                        icon: (
                          <div
                            style={{
                              display: "inline",
                              marginRight: 16,
                              marginLeft: 3,
                            }}
                          >
                            𝕏
                          </div>
                        ),
                        as: Link,
                        target: "_blank",
                        to: `https://x.com/intent/tweet?text=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea._id}`)}`,
                      },
                      {
                        key: "share-facebook",
                        value: "share-facebook",
                        text: "Share on Facebook",
                        icon: "facebook",
                        as: Link,
                        target: "_blank",
                        to: `https://www.facebook.com/sharer/sharer.php?u=${util.getBaseUrl(user?.ownerOrganisation, `/ideas/${idea._id}`)}`,
                      },
                      {
                        key: "copy-link",
                        value: "copy-link",
                        text: `Copy link to ${t("generic.idea")}`,
                        icon: "copy",
                        onClick: () => copyLink(),
                        onKeyDown: handleClickForKeyboard(() => copyLink()),
                      },
                    ]}
                  />
                </div>
              </AccessibleModal.Content>
            </>}
        </div>
        <AccessibleModal.Actions>
          <Button
            content={t("generic.close")}
            onClick={() => setSubmitModalOpen(false)}
          />
          {(
            idea?.ownerChallenge &&
            idea.ownerChallenge.ideaVisibility === "users"
          ) ?
            <Button
              primary
              content={`View ${t("generic.ideas")} created by others`}
              onClick={() => {
                setSubmitModalOpen(false);
                navigate(`/challenges/${idea.ownerChallenge._id}/ideas`);
              }}
            />
          : <Button
              content={`Return to my ${t("generic.idea")}`}
              onClick={() => setSubmitModalOpen(false)}
            />
          }
        </AccessibleModal.Actions>
      </AccessibleModal>

      {canEditIdea || canManageChallenge || canUpdateIdeaStatus ?
        idea.isSubmitted ?
          <StyledButton
            style={style}
            size={size}
            content={
              canEditIdea ?
                theme.sizes.isMobile || canEditIdeas || canManageChallenges ?
                  `Unsubmit ${t("generic.idea")}`
                : t("ideas.editing")
              : t("generic.unsubmit")
            }
            icon={canEditIdeas || canManageChallenges ? "reply" : "edit"}
            onClick={() => submitIdea(false)}
            primary
            loading={isSubmitting}
            className="submit-idea-button"
          />
        : <TooltipButton
            disabled={
              isSaving ||
              unsaved ||
              (!idea?.ownerChallenge && !idea?.isSubmitted) ||
              (!isSubmissible && !canSubmitWithRequiredFieldsMissing) ||
              ((!idea.submissionDeadline ||
                (idea.submissionDeadline &&
                  isBefore(
                    new Date(idea.submissionDeadline?.deadline),
                    new Date(),
                  ))) &&
                idea?.ownerChallenge?.stage === "closed")
            }
            style={style}
            size={size}
            fluid={!isStatusBar}
            content={t("generic.submit")}
            icon={isSubmitting ? "spinner" : "paper plane"}
            onClick={openSubmissionWizard}
            primary
            loading={isSubmitting}
            className="submit-idea-button"
            tooltip={tooltip}
            tooltipProps={{
              position: isStatusBar ? "bottom right" : "top center",
              style: { maxWidth: 400 },
            }}
          />

      : null}
    </>
  );
};

export default SubmitIdeaButton;
